import type { HierarcicalObjectReference } from "@novorender/data-js-api";
import type { Dispatch, SetStateAction } from "react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Events, track } from "apis/procore";
import type { Tree } from "components/objectTree/types";
import { buildFilteredTree } from "components/window/core/objectTree/utils";
import { usePreloadedObjectTreeRef, usePreloadedObjectTreeRootReady } from "contexts/objectTree";
import type { FilteredTree, ObjectDataAccess } from "dataAccess";
import type { ModelTreeNodeData } from "features/modelTree/types";
import { emptyModelTree } from "features/modelTree/utils";
import { telemetry } from "telemetry";
import { registerMemoryStatsProvider } from "utils/memoryDiagnostics";
import { startMeasure } from "utils/performanceMeasure";

const MINIMUM_SEARCH_TERM_LENGTH = 2;
const DEFAULT_SEARCH_DEBOUNCE_MS = 300;

/**
 * Upper bound on how many search matches are collected. This was the last
 * unbounded allocation on the object tree path: every match is held in an
 * array, gets a node in the filtered tree (plus its ancestors) and
 * an entry in the filtered-ID set, so a broad term on a huge model could
 * retain hundreds of thousands of objects at once. Capping keeps the retained
 * set bounded; the UI shows a "showing first N results" notice so the user
 * knows to refine the term. Aligned with the tree's other budgets
 * (loadChildren's maxScan, MAX_PRELOAD_NODES).
 */
export const MAX_SEARCH_RESULTS = 5000;

declare global {
    interface Window {
        /**
         * Manual-testing override for the search result cap, read per search.
         * Set `window.__NOVO_SEARCH_CAP__ = Infinity` in the console to
         * reproduce the pre-cap ("before") behavior for memory comparisons,
         * then `delete window.__NOVO_SEARCH_CAP__` to restore the default.
         */
        __NOVO_SEARCH_CAP__?: number;
    }
}

/** Effective cap for one search: console override (if set) wins over the hook's cap. */
function getEffectiveMaxResults(maxResults: number): number {
    if (typeof window !== "undefined" && typeof window.__NOVO_SEARCH_CAP__ === "number") {
        return window.__NOVO_SEARCH_CAP__;
    }
    return maxResults;
}

/**
 * Distinguishes memory-stats providers when several hook instances are mounted
 * at once (Object Tree window + Objects side panel). Same-name registrations
 * replace each other, so without a unique suffix the reporting instance could
 * be a different one than the user is searching in.
 */
let memoryStatsInstanceCounter = 0;

export type UseObjectTreeSearchReturn = {
    searchTerm: string;
    filteredObjectIds: Set<number> | null;
    tree: Tree<ModelTreeNodeData>;
    setTree: Dispatch<SetStateAction<Tree<ModelTreeNodeData>>>;
    treeKey: number;
    isSearching: boolean;
    hasNoResults: boolean;
    searchError: string | null;
    /** True when the tree is actively filtered by a search (results present, in-flight, or errored). */
    isFilterActive: boolean;
    /** True when the current results were cut off at {@link MAX_SEARCH_RESULTS} matches. */
    resultsTruncated: boolean;
    handleSearchChange: (value: string) => void;
    handleSearchSubmit: () => Promise<void>;
    clearSearchState: () => void;
    /**
     * Fresh copy of the base tree (preloaded snapshot, or empty if preload hasn't produced a
     * root yet). Same source `clearSearchState` restores to, exposed so callers can seed
     * additional loads (e.g. a critical-path expansion for a drilled-into search result)
     * against the exact tree that will be current after the search state is cleared.
     */
    getBaseTree: () => Tree<ModelTreeNodeData>;
};

/**
 * Manages object tree search state and behavior.
 *
 * Used by ObjectTreeWindow to provide consistent
 * search UX across all object tree instances.
 *
 * Search is debounced: typing triggers a search automatically after a short pause
 * (`toleranceMs`, which defaults to {@link DEFAULT_SEARCH_DEBOUNCE_MS}), so users get
 * live filtering without a separate Enter press.
 * Pressing Enter / clicking the search icon fires immediately (cancelling any pending
 * debounce), matching the behaviour of the Properties panel search.
 *
 * All in-flight search iterators are aborted via AbortController when a new search
 * starts, when the input is cleared, or when the component unmounts.
 *
 * @param toleranceMs - How long to wait after the last keystroke before firing the
 *   remote search. Defaults to {@link DEFAULT_SEARCH_DEBOUNCE_MS}. Increase for slower
 *   connections or decrease for snappier local indices.
 * @param maxResults - Upper bound on collected search matches. Defaults to
 *   {@link MAX_SEARCH_RESULTS}; production callers should not override it. Exists so
 *   the memory before/after test can run the identical code path uncapped
 *   (`Infinity` reproduces the pre-cap behavior) and compare retained structures.
 */
export function useObjectTreeSearch(
    dataAccess: ObjectDataAccess | undefined,
    toleranceMs: number = DEFAULT_SEARCH_DEBOUNCE_MS,
    maxResults: number = MAX_SEARCH_RESULTS,
): UseObjectTreeSearchReturn {
    const { t } = useTranslation();

    // Tree built ahead of first open by the background preload (top categories +
    // deeper folders loaded during idle time). Seed the base (unfiltered) tree
    // from its current snapshot so opening the panel renders instantly; fall back
    // to emptyModelTree if the preload hasn't produced a root yet (the tree then
    // loads its root on mount as before).
    const preloadedTreeRef = usePreloadedObjectTreeRef();
    // Shallow-copy the map so subsequent in-place child additions (appendDescendants)
    // don't mutate the shared preloaded tree, which is reused across consumers and
    // when resetting after a search.
    const baseTree = (): Tree<ModelTreeNodeData> => ({ ...(preloadedTreeRef?.current ?? emptyModelTree) });

    const [searchTerm, setSearchTerm] = useState("");
    const [filteredObjectIds, setFilteredObjectIds] = useState<Set<number> | null>(null);
    const [tree, setTree] = useState<Tree<ModelTreeNodeData>>(baseTree);
    const [treeKey, setTreeKey] = useState(0);
    const [isSearching, setIsSearching] = useState(false);
    const [hasNoResults, setHasNoResults] = useState(false);
    const [searchError, setSearchError] = useState<string | null>(null);
    const [resultsTruncated, setResultsTruncated] = useState(false);

    // The preloaded tree's root can still be loading at the moment this hook first
    // mounts (Phase 1 is an async fetch, and the panel can open before it resolves) —
    // in that case `baseTree()` above seeded from an empty tree, and there is no other
    // opportunity to pick up the preload, since consumers only re-seed from the ref
    // explicitly (on mount / after clearing a search). Adopt the preloaded snapshot the
    // moment it becomes ready, but only if nothing has happened yet that a wholesale
    // tree swap would clobber (a search in progress/active, or the tree already having
    // started loading on its own via the no-preload-available fallback).
    const preloadedRootReady = usePreloadedObjectTreeRootReady();
    useEffect(() => {
        if (!preloadedRootReady) {
            return;
        }
        setTree((current) => (current[""]?.children === undefined && !searchTerm ? baseTree() : current));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [preloadedRootReady]);

    const searchAbortController = useRef<AbortController>(new AbortController());
    const debounceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Retained-structure stats for the last applied search, reported through the
    // on-demand memory diagnostics (window.__NOVO_MEMORY__) so heap before/after
    // comparisons can be attributed to concrete structures.
    const lastSearchStatsRef = useRef<{ matchCount: number; truncated: boolean; filteredTreeNodeCount: number } | null>(
        null,
    );
    const treeRef = useRef(tree);
    treeRef.current = tree;
    const filteredIdsRef = useRef(filteredObjectIds);
    filteredIdsRef.current = filteredObjectIds;

    useEffect(() => {
        return registerMemoryStatsProvider(`objectTreeSearch#${++memoryStatsInstanceCounter}`, () => ({
            treeNodeCount: Object.keys(treeRef.current).length,
            filteredIdCount: filteredIdsRef.current?.size ?? 0,
            lastSearch: lastSearchStatsRef.current,
            maxSearchResults: MAX_SEARCH_RESULTS,
        }));
    }, []);

    // Always-fresh ref so debounce closure calls the latest performSearch even
    // if the callback identity changed (e.g. the backend became available after mount).
    const performSearchRef = useRef<(term: string) => Promise<boolean>>(async () => false);

    const abortSearch = useCallback(() => {
        searchAbortController.current.abort();
        searchAbortController.current = new AbortController();
    }, []);

    const clearDebounce = useCallback(() => {
        if (debounceTimerRef.current !== null) {
            clearTimeout(debounceTimerRef.current);
            debounceTimerRef.current = null;
        }
    }, []);

    const clearSearchState = useCallback(() => {
        clearDebounce();
        abortSearch();
        setIsSearching(false);
        setFilteredObjectIds(null);
        setTree({ ...(preloadedTreeRef?.current ?? emptyModelTree) });
        setTreeKey((prev) => prev + 1);
        setHasNoResults(false);
        setSearchError(null);
        setResultsTruncated(false);
        lastSearchStatsRef.current = null;
    }, [abortSearch, clearDebounce, preloadedTreeRef]);

    const searchDatabase = useCallback(
        async (term: string, abortSignal: AbortSignal): Promise<FilteredTree> => {
            if (!dataAccess) {
                return { objects: [], truncated: false };
            }

            // Node names are URL-encoded in the database (e.g. "Flanged Square Bend %2F Tee"),
            // so encode "/" in the search term to match them. The `contains` operator is
            // case-insensitive, so "%2F" matches both "%2f" and "%2F" stored values.
            const dbTerm = term.replaceAll("/", "%2F");
            return dataAccess.getFilteredTree(
                { terms: [{ property: "name", contains: dbTerm }] },
                { maxResults: getEffectiveMaxResults(maxResults), signal: abortSignal },
            );
        },
        [dataAccess, maxResults],
    );

    // Every real top-level folder (one per file, typically) known so far, regardless of
    // whether it has a search match beneath it, so buildFilteredTree can always surface the
    // full set of files at the top level instead of only the ones a match happened to fall
    // under. Reads from two sources since either can have root's children loaded first:
    //  - the current (pre-search) tree — populated either by the preload adoption effect
    //    above, or (if preload hasn't produced a root yet) by the panel's own on-mount fetch;
    //  - the preloaded tree ref directly, as a fallback for the case where neither has
    //    happened on `tree` yet but the preload has since moved ahead of it.
    // Both are cheap in-memory reads; the search itself already only ever needs re-running
    // this once per query, not per render.
    const getTopLevelObjects = useCallback((): HierarcicalObjectReference[] => {
        const seen = new Set<string>();
        const objects: HierarcicalObjectReference[] = [];
        for (const source of [tree, preloadedTreeRef?.current]) {
            for (const path of source?.[""]?.children ?? []) {
                if (seen.has(path)) {
                    continue;
                }
                const obj = source?.[path]?.data.object;
                if (obj) {
                    seen.add(path);
                    objects.push(obj);
                }
            }
        }
        return objects;
    }, [tree, preloadedTreeRef]);

    const updateSearchState = useCallback(
        ({ objects, truncated }: FilteredTree, matchIds: Set<number>): { treeNodeCount: number } => {
            if (matchIds.size === 0) {
                setFilteredObjectIds(null);
                setTree({ ...(preloadedTreeRef?.current ?? emptyModelTree) });
                setHasNoResults(true);
                setResultsTruncated(false);
                return { treeNodeCount: 0 };
            }

            const filteredTree = buildFilteredTree(objects, getTopLevelObjects());
            setFilteredObjectIds(matchIds);
            setTree(filteredTree);
            setHasNoResults(false);
            setResultsTruncated(truncated);
            return { treeNodeCount: Object.keys(filteredTree).length };
        },
        [preloadedTreeRef, getTopLevelObjects],
    );

    const performSearch = useCallback(
        async (term: string): Promise<boolean> => {
            if (!dataAccess) {
                return false;
            }

            if (!term || term.length < MINIMUM_SEARCH_TERM_LENGTH) {
                return false;
            }

            abortSearch();
            // Capture the controller/signal for *this* search so that a later
            // call to abortSearch() (which replaces searchAbortController.current)
            // does not cause stale searches to mis-identify themselves as current.
            const localController = searchAbortController.current;
            const localSignal = localController.signal;

            setIsSearching(true);
            setHasNoResults(false);
            setSearchError(null);

            const perf = startMeasure("objectTreeSearch");
            try {
                const result = await searchDatabase(term, localSignal);
                const matchIds = new Set(result.objects.filter((obj) => obj.isMatch).map((obj) => obj.id));
                const { truncated } = result;
                perf.mark("drainComplete", { matchCount: matchIds.size, truncated });

                // Only update state if this search hasn't been superseded.
                if (!localSignal.aborted) {
                    const { treeNodeCount } = updateSearchState(result, matchIds);
                    lastSearchStatsRef.current = {
                        matchCount: matchIds.size,
                        truncated,
                        filteredTreeNodeCount: treeNodeCount,
                    };
                    perf.end({ matchCount: matchIds.size, truncated, treeNodeCount });
                    return true;
                }
                perf.end({ aborted: true });
            } catch (error) {
                perf.end({ error: true, aborted: localSignal.aborted });
                if (!localSignal.aborted) {
                    console.error("Search error:", error);
                    telemetry.trackCaughtException({
                        source: "useObjectTreeSearch.performSearch",
                        category: "data",
                        error,
                        severity: "error",
                    });
                    setSearchError(t("errorLoadingSearchResults"));
                    setFilteredObjectIds(null);
                    setTree({ ...(preloadedTreeRef?.current ?? emptyModelTree) });
                    setHasNoResults(false);
                    setResultsTruncated(false);
                }
            } finally {
                // Only clear the loading indicator if this search is still current.
                if (!localSignal.aborted) {
                    setIsSearching(false);
                }
            }

            // Search was aborted or superseded — results were not applied.
            return false;
        },
        [dataAccess, abortSearch, searchDatabase, updateSearchState, t, preloadedTreeRef],
    );

    // Keep the ref in sync so the debounce closure always uses the latest version.
    performSearchRef.current = performSearch;

    const handleSearchChange = useCallback(
        (value: string) => {
            setSearchTerm(value);
            clearDebounce();

            // Abort any in-flight search immediately so its results are discarded
            // and network/CPU resources are freed.
            abortSearch();

            // Normalize for length check and search — trim so that debounced
            // searches are consistent with Enter/submit (which also trims).
            const trimmed = value.trim();

            if (trimmed.length === 0) {
                clearSearchState();
                return;
            }

            // Don't schedule a search for values shorter than the minimum.
            // If a previous search produced results, errored, or is still
            // in-flight, clear/abort immediately so the UI never shows stale
            // results that don't match the current (too-short) term.
            if (trimmed.length < MINIMUM_SEARCH_TERM_LENGTH) {
                if (filteredObjectIds !== null || hasNoResults || searchError !== null || isSearching) {
                    clearSearchState();
                }
                return;
            }

            debounceTimerRef.current = setTimeout(() => {
                debounceTimerRef.current = null;
                void performSearchRef.current(trimmed);
            }, toleranceMs);
        },
        [
            clearDebounce,
            abortSearch,
            clearSearchState,
            toleranceMs,
            filteredObjectIds,
            hasNoResults,
            searchError,
            isSearching,
        ],
    );

    // Pressing Enter or clicking the search icon fires immediately without waiting
    // for the debounce, cancelling any pending debounce first.
    const handleSearchSubmit = useCallback(async () => {
        clearDebounce();
        const term = searchTerm.trim();

        if (term.length < MINIMUM_SEARCH_TERM_LENGTH) {
            clearSearchState();
            return;
        }

        const searched = await performSearch(term);
        if (searched) {
            void track(Events.objectTree.searched, { search_term: term });
        }
    }, [clearDebounce, clearSearchState, performSearch, searchTerm]);

    // Cleanup on unmount: cancel debounce and abort any in-flight search.
    useEffect(() => {
        return () => {
            clearDebounce();
            searchAbortController.current.abort();
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const isFilterActive = filteredObjectIds !== null || isSearching || hasNoResults || searchError !== null;

    return {
        searchTerm,
        filteredObjectIds,
        tree,
        setTree,
        treeKey,
        isSearching,
        hasNoResults,
        searchError,
        isFilterActive,
        resultsTruncated,
        handleSearchChange,
        handleSearchSubmit,
        clearSearchState,
        getBaseTree: baseTree,
    };
}
